"use client";

import { Suspense, useState } from "react";
import { useRouter } from "next/navigation";
import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import { z } from "zod";

import { clientAuth } from "@billingnow/utilities/auth-client";
import { detectClientLanguage } from "@billingnow/utilities/language";
import { parseCatchError } from "@/utils";

import {
  AppleLogo,
  AuthLeftPanel,
  AuthPageSkeleton,
  AuthTermsFooter,
  AuthUtilitiesRow,
  EMAIL_RE,
  GoogleLogo,
  inputClass,
  labelClass,
  nunitoSans,
  primaryBtnClass,
  secondaryBtnClass,
} from "../auth-ui";
import { cn } from "@/utils";

const signupSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(8, "Password must be at least 8 characters long"),
});

type SignupSchema = z.infer<typeof signupSchema>;

function SignupForm() {
  const t = useTranslations("auth.signup");
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();

  const { register, handleSubmit, watch } = useForm<SignupSchema>({
    resolver: zodResolver(signupSchema),
    defaultValues: { email: "", password: "" },
  });

  const email = watch("email");
  const password = watch("password");
  const canSignup = EMAIL_RE.test(email) && password.length >= 8;

  // Persist the detected language as a cookie so the OAuth callback server-side
  // hook can read it (the user-create hook can't see navigator.language).
  const captureLanguageCookie = () => {
    const lang = detectClientLanguage();
    document.cookie = `locale=${lang}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax`;
    return lang;
  };

  const onSubmit = async (data: SignupSchema) => {
    setIsLoading(true);
    try {
      const language = captureLanguageCookie();
      // The display name is collected in the onboarding wizard's first step,
      // not here. `x-password-chosen` tells the server the user typed this
      // password themselves (sets users.has_password, unlike the legacy
      // random-password signups).
      const result = await clientAuth.signUp.email({
        email: data.email,
        name: "",
        password: data.password,
        language,
        fetchOptions: {
          headers: { "x-password-chosen": "true" },
        },
      });

      if (result.error) {
        toast.error(result.error?.message || t("toastSignUpFailed"));
        return;
      }

      // autoSignIn is enabled server-side, so the signup response already set
      // the session cookie - go straight into the app.
      toast.success(t("toastAccountCreated"));
      router.push("/");
    } catch (error) {
      toast.error(parseCatchError(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    setIsLoading(true);
    try {
      captureLanguageCookie();
      await clientAuth.signIn.social({
        provider: "google",
        callbackURL: "/",
      });
    } catch (error) {
      toast.error(parseCatchError(error));
      setIsLoading(false);
    }
  };

  const handleAppleSignup = async () => {
    setIsLoading(true);
    try {
      captureLanguageCookie();
      await clientAuth.signIn.social({
        provider: "apple",
        callbackURL: "/",
      });
    } catch (error) {
      toast.error(parseCatchError(error));
      setIsLoading(false);
    }
  };

  return (
    <div
      className={cn(
        nunitoSans.className,
        "signin-scope relative flex min-h-screen w-full flex-wrap overflow-hidden bg-[color:var(--si-page)] text-[color:var(--si-ink)] transition-colors duration-300",
      )}
    >
      <AuthLeftPanel namespace="auth.signup" footerKey="freeToStart" />

      {/* Right auth panel */}
      <div className="relative box-border flex min-w-[320px] flex-[1_1_460px] flex-col px-[clamp(24px,5vw,72px)] py-[clamp(24px,4vw,44px)]">
        <div className="mx-auto flex w-full max-w-[440px] flex-1 flex-col justify-center">
          <div
            data-signin-anim
            style={{ animation: "signin-step-in .5s cubic-bezier(.22,1,.36,1) both" }}
            className="flex flex-col"
          >
            {/* Header */}
            <h2 className="m-0 text-[clamp(24px,3vw,30px)] font-extrabold text-[color:var(--si-ink)]">{t("title")}</h2>
            <p className="mb-0 mt-2 text-[15px] text-[color:var(--si-body-2)]">{t("subtitle")}</p>

            {/* Google + Apple */}
            <div className="mt-8 flex flex-col gap-3">
              <button type="button" onClick={handleGoogleSignup} disabled={isLoading} className={secondaryBtnClass}>
                <GoogleLogo />
                {t("continueWithGoogle")}
              </button>
              <button type="button" onClick={handleAppleSignup} disabled={isLoading} className={secondaryBtnClass}>
                <AppleLogo />
                {t("continueWithApple")}
              </button>
            </div>

            {/* Divider */}
            <div className="my-[26px] flex items-center gap-4">
              <div className="h-px flex-1 bg-[color:var(--si-hairline)]" />
              <span className="text-xs font-bold uppercase tracking-[1px] text-[color:var(--si-muted)]">{t("or")}</span>
              <div className="h-px flex-1 bg-[color:var(--si-hairline)]" />
            </div>

            {/* Email + password */}
            <form onSubmit={handleSubmit(onSubmit)}>
              <label htmlFor="email" className={labelClass}>
                {t("emailLabel")}
              </label>
              <input
                id="email"
                type="email"
                placeholder={t("emailPlaceholder")}
                {...register("email")}
                disabled={isLoading}
                className={inputClass}
              />

              <label htmlFor="password" className={cn(labelClass, "mt-[18px]")}>
                {t("passwordLabel")}
              </label>
              <input
                id="password"
                type="password"
                placeholder={t("passwordPlaceholder")}
                {...register("password")}
                disabled={isLoading}
                className={inputClass}
              />

              <button type="submit" disabled={isLoading || !canSignup} className={primaryBtnClass}>
                {isLoading ? t("creatingAccount") : t("createAccount")}
              </button>
            </form>

            {/* Sign In Link */}
            <p className="mt-[22px] text-center text-sm text-[color:var(--si-body-2)]">
              {t("hasAccount")}{" "}
              <a href="/signin" className="font-extrabold text-[color:var(--si-ink)] no-underline">
                {t("signIn")}
              </a>
            </p>

            <AuthTermsFooter namespace="auth.signup" />
          </div>
        </div>

        <AuthUtilitiesRow />
      </div>
    </div>
  );
}

export default function SignupPage() {
  return (
    <Suspense fallback={<AuthPageSkeleton />}>
      <SignupForm />
    </Suspense>
  );
}
